import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { ChatBubbleLeftEllipsisIcon, UsersIcon } from '@heroicons/react/24/outline';
import { bookmarkService } from '../services/bookmarkService';

const STATUS_FILTERS = ['all', 'pending', 'invited', 'registered'];

const statusBadge = (status) => {
  const styles = {
    pending:    'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400',
    invited:    'bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-400',
    registered: 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400',
  };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${styles[status] || 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300'}`}>
      {status}
    </span>
  );
};

const AdminSettings = () => {
  const [senderEmail, setSenderEmail] = useState('');
  const [config, setConfig] = useState({
    enabled: false,
    reportEmail: '',
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  const [waitlist, setWaitlist] = useState([]);
  const [waitlistLoading, setWaitlistLoading] = useState(true);
  const [waitlistError, setWaitlistError] = useState('');
  const [selectedEmails, setSelectedEmails] = useState(new Set());
  const [inviting, setInviting] = useState(false);
  const [inviteResult, setInviteResult] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');

  const loadWaitlist = useCallback(() => {
    setWaitlistLoading(true);
    setWaitlistError('');
    bookmarkService.getWaitlist()
      .then(data => setWaitlist(data.items || []))
      .catch(() => setWaitlistError('Failed to load waitlist.'))
      .finally(() => setWaitlistLoading(false));
  }, []);

  useEffect(() => {
    bookmarkService.getAdminConfig()
      .then(data => {
        setSenderEmail(data.senderEmail || '');
        setConfig(data.dailyReport || { enabled: false, reportEmail: '' });
      })
      .catch(() => setError('Failed to load configuration.'))
      .finally(() => setLoading(false));
    loadWaitlist();
  }, [loadWaitlist]);

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    setSuccessMessage('');
    try {
      await bookmarkService.updateAdminConfig({ senderEmail, dailyReport: config });
      setSuccessMessage('Configuration saved.');
    } catch {
      setError('Failed to save configuration.');
    } finally {
      setSaving(false);
    }
  };

  const pendingItems     = waitlist.filter(e => e.status === 'pending');
  const invitedItems     = waitlist.filter(e => e.status === 'invited');
  const registeredItems  = waitlist.filter(e => e.status === 'registered');
  const conversionRate   = invitedItems.length + registeredItems.length > 0
    ? Math.round((registeredItems.length / (invitedItems.length + registeredItems.length)) * 100)
    : 0;

  const filteredWaitlist = statusFilter === 'all'
    ? waitlist
    : waitlist.filter(e => e.status === statusFilter);

  const toggleSelect = (email) => {
    setSelectedEmails(prev => {
      const next = new Set(prev);
      if (next.has(email)) next.delete(email); else next.add(email);
      return next;
    });
  };

  const selectAllPending = () => {
    setSelectedEmails(new Set(pendingItems.map(e => e.email)));
  };

  const handleInvite = async () => {
    if (selectedEmails.size === 0) return;
    setInviting(true);
    setInviteResult(null);
    try {
      const result = await bookmarkService.inviteUsers([...selectedEmails]);
      setInviteResult(result);
      setSelectedEmails(new Set());
      loadWaitlist();
    } catch {
      setInviteResult({ error: 'Failed to send invites. Check Admin Settings sender email.' });
    } finally {
      setInviting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
      </div>
    );
  }

  return (
    <div className="max-w-2xl">
      <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-6">Admin Settings</h1>

      {/* ── Quick links ── */}
      <div className="flex flex-wrap gap-3 mb-6">
        <Link
          to="/admin/feedback"
          className="flex items-center gap-2 px-4 py-2.5 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-sm font-medium text-gray-700 dark:text-gray-300 hover:border-primary-300 hover:text-primary-600 dark:hover:text-primary-400 transition-colors shadow-sm"
        >
          <ChatBubbleLeftEllipsisIcon className="h-4 w-4" />
          View Feedback
        </Link>
        <Link
          to="/admin/users"
          className="flex items-center gap-2 px-4 py-2.5 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-sm font-medium text-gray-700 dark:text-gray-300 hover:border-primary-300 hover:text-primary-600 dark:hover:text-primary-400 transition-colors shadow-sm"
        >
          <UsersIcon className="h-4 w-4" />
          User Stats
        </Link>
      </div>

      {/* ── Email Settings ── */}
      <section className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6 mb-6">
        <h2 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-1">Email Settings</h2>
        <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">Used as the sender address for all system emails — invites, daily reports, and weekly digests.</p>
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Sender email</label>
          <input
            type="email"
            value={senderEmail}
            onChange={e => setSenderEmail(e.target.value)}
            placeholder="noreply@yourdomain.com"
            className="form-input"
          />
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">Must be a verified sender identity in Amazon SES.</p>
        </div>
        {error && <p className="mt-3 text-sm text-red-600 dark:text-red-400">{error}</p>}
        {successMessage && <p className="mt-3 text-sm text-green-600 dark:text-green-400">{successMessage}</p>}
        <div className="flex justify-end pt-4">
          <button type="button" onClick={handleSave} disabled={saving} className="btn-primary">
            {saving ? 'Saving...' : 'Save'}
          </button>
        </div>
      </section>

      {/* ── Beta Waitlist ── */}
      <section className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6 mb-6">
        <h2 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">Beta Waitlist</h2>

        {/* Conversion metrics */}
        {!waitlistLoading && waitlist.length > 0 && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
            {[
              { label: 'Total', value: waitlist.length },
              { label: 'Pending', value: pendingItems.length, color: 'text-yellow-600' },
              { label: 'Invited', value: invitedItems.length, color: 'text-primary-600' },
              { label: 'Registered', value: registeredItems.length, color: 'text-green-600' },
            ].map(({ label, value, color }) => (
              <div key={label} className="bg-gray-50 dark:bg-gray-700 rounded-lg p-3 text-center">
                <p className={`text-xl font-bold ${color || 'text-gray-900 dark:text-gray-100'}`}>{value}</p>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{label}</p>
              </div>
            ))}
          </div>
        )}

        {!waitlistLoading && invitedItems.length + registeredItems.length > 0 && (
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
            Conversion rate: <span className="font-medium text-gray-700 dark:text-gray-200">{conversionRate}%</span>
            {' '}of invited users have registered
          </p>
        )}

        {waitlistError && <p className="text-sm text-red-600 dark:text-red-400 mb-3">{waitlistError}</p>}

        {waitlistLoading && (
          <p className="text-sm text-gray-400 dark:text-gray-500">Loading...</p>
        )}

        {!waitlistLoading && waitlist.length === 0 && (
          <p className="text-sm text-gray-500 dark:text-gray-400">No waitlist entries yet.</p>
        )}

        {!waitlistLoading && waitlist.length > 0 && (
          <>
            {/* Status filter tabs */}
            <div className="flex flex-wrap gap-1 mb-3 border-b border-gray-100 dark:border-gray-700 pb-3">
              {STATUS_FILTERS.map(f => (
                <button
                  key={f}
                  type="button"
                  onClick={() => setStatusFilter(f)}
                  className={`px-3 py-1 rounded-full text-xs font-medium transition-colors capitalize ${
                    statusFilter === f
                      ? 'bg-primary-600 text-white'
                      : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                  }`}
                >
                  {f === 'all' ? `All (${waitlist.length})` : `${f} (${waitlist.filter(e => e.status === f).length})`}
                </button>
              ))}
            </div>

            {/* Waitlist entries */}
            <div className="space-y-1 mb-4 max-h-64 overflow-y-auto">
              {filteredWaitlist.length === 0 && (
                <p className="text-sm text-gray-400 dark:text-gray-500 py-4 text-center">No entries with this status.</p>
              )}
              {filteredWaitlist.map(entry => (
                <label key={entry.email} className={`flex items-center gap-3 p-2 rounded cursor-pointer ${entry.status === 'pending' ? 'hover:bg-gray-50 dark:hover:bg-gray-700' : 'cursor-default'}`}>
                  <input
                    type="checkbox"
                    checked={selectedEmails.has(entry.email)}
                    onChange={() => toggleSelect(entry.email)}
                    disabled={entry.status !== 'pending'}
                    className="h-4 w-4 rounded border-gray-300 text-primary-600"
                  />
                  <span className="flex-1 text-sm text-gray-800 dark:text-gray-200 truncate">{entry.email}</span>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    {entry.invitedAt ? (
                      <span className="text-xs text-gray-400 dark:text-gray-500" title="Invited">
                        invited {new Date(entry.invitedAt).toLocaleDateString()}
                      </span>
                    ) : (
                      <span className="text-xs text-gray-400 dark:text-gray-500">
                        {new Date(entry.createdAt).toLocaleDateString()}
                      </span>
                    )}
                    {statusBadge(entry.status)}
                  </div>
                </label>
              ))}
            </div>

            <div className="flex flex-wrap items-center justify-between gap-2">
              <button
                type="button"
                onClick={selectAllPending}
                disabled={pendingItems.length === 0}
                className="text-sm text-primary-600 dark:text-primary-400 hover:underline disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Select all pending
              </button>
              <button
                type="button"
                onClick={handleInvite}
                disabled={selectedEmails.size === 0 || inviting}
                className="btn-primary"
              >
                {inviting ? 'Inviting...' : `Invite Selected (${selectedEmails.size})`}
              </button>
            </div>

            {inviteResult && !inviteResult.error && (
              <div className="mt-3 text-sm">
                {inviteResult.invited?.length > 0 && (
                  <p className="text-green-600 dark:text-green-400">Invited: {inviteResult.invited.join(', ')}</p>
                )}
                {inviteResult.failed?.length > 0 && (
                  <p className="text-red-600 dark:text-red-400 mt-1">
                    Failed: {inviteResult.failed.map(f => `${f.email} (${f.reason})`).join(', ')}
                  </p>
                )}
              </div>
            )}
            {inviteResult?.error && (
              <p className="mt-3 text-sm text-red-600 dark:text-red-400">{inviteResult.error}</p>
            )}
          </>
        )}
      </section>

      {/* ── Daily Email Report ── */}
      <section className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
        <h2 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">Daily Email Report</h2>

        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Enable daily report</label>
            <button
              type="button"
              onClick={() => setConfig(c => ({ ...c, enabled: !c.enabled }))}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none ${
                config.enabled ? 'bg-primary-600' : 'bg-gray-300 dark:bg-gray-600'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  config.enabled ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Send report to
            </label>
            <input
              type="email"
              value={config.reportEmail}
              onChange={e => setConfig(c => ({ ...c, reportEmail: e.target.value }))}
              placeholder="admin@example.com"
              className="form-input"
            />
          </div>

          <div className="flex justify-end pt-2">
            <button type="button" onClick={handleSave} disabled={saving} className="btn-primary">
              {saving ? 'Saving...' : 'Save'}
            </button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AdminSettings;
